import { AclResourceType } from './acl-resource-type.model';

export enum AclApiPermission {
  AdministrateUsers = 'administrate_users',
  BypassSso = 'bypass_sso',
  ChangePassword = 'change_password',
  CommitArbitraryFiles = 'commit_arbitrary_files',
  Create = 'create',
  Checkout = 'checkout',
  CreateApiKeys = 'create_api_keys',
  CreateSpaces = 'create_spaces',
  CreateTeams = 'create_teams',
  Delete = 'delete',
  DeleteOrg = 'delete_org',
  GrantAdministrator = 'grant_administrator',
  GrantBillingAdministrator = 'grant_billing_administrator',
  GrantCreator = 'grant_creator',
  GrantEditor = 'grant_editor',
  GrantGuest = 'grant_guest',
  GrantManager = 'grant_manager',
  GrantMember = 'grant_member',
  GrantOwner = 'grant_owner',
  GrantViewer = 'grant_viewer',
  Impersonate = 'impersonate',
  PerformResourceEnumerationAs = 'perform_resource_enumeration_as',
  Read = 'read',
  ReadAllResources = 'read_all_resources',
  ReadBilling = 'read_billing',
  ReadKey = 'read_key',
  ReadOrgData = 'read_org_data',
  ReadSpaceSettings = 'read_space_settings',
  ReadUnderlyingData = 'read_underlying_data',
  ReadSubjects = 'read_subjects',
  ReceiveGrants = 'receive_grants',
  RotateKey = 'rotate_key',
  Run = 'run',
  ViewModule = 'view_module',
  Write = 'write',
  WriteBilling = 'write_billing',
  WriteOrgData = 'write_org_data',
  WriteDwhCredentials = 'write_dwh_credentials',
  WriteSpaceSettings = 'write_space_settings',
}

export enum AclAssetModulePermission {
  Create = AclApiPermission.Create,
  GrantCreator = AclApiPermission.GrantCreator,
  GrantEditor = AclApiPermission.GrantEditor,
  GrantMember = AclApiPermission.GrantMember,
  GrantOwner = AclApiPermission.GrantOwner,
  GrantViewer = AclApiPermission.GrantViewer,
  ReadAllResources = AclApiPermission.ReadAllResources,
  ReadSubjects = AclApiPermission.ReadSubjects,
  ViewModule = AclApiPermission.ViewModule,
}

export const AclNonWritableResourcePermission = {
  Delete: AclApiPermission.Delete,
  GrantEditor: AclApiPermission.GrantEditor,
  GrantOwner: AclApiPermission.GrantOwner,
  GrantViewer: AclApiPermission.GrantViewer,
  Read: AclApiPermission.Read,
  ReadSubjects: AclApiPermission.ReadSubjects,
} as const;
export type AclNonWritableResourcePermission =
  (typeof AclNonWritableResourcePermission)[keyof typeof AclNonWritableResourcePermission];

export const AclAssetPermission = {
  ...AclNonWritableResourcePermission,
  Write: AclApiPermission.Write,
} as const;
export type AclAssetPermission = (typeof AclAssetPermission)[keyof typeof AclAssetPermission];

export const AclRunnableAssetPermission = {
  ...AclAssetPermission,
  Run: AclApiPermission.Run,
} as const;
export type AclRunnableAssetPermission = (typeof AclRunnableAssetPermission)[keyof typeof AclRunnableAssetPermission];

export enum AclOrgPermission {
  AdministrateUsers = AclApiPermission.AdministrateUsers,
  BypassSso = AclApiPermission.BypassSso,
  CreateApiKeys = AclApiPermission.CreateApiKeys,
  CreateSpaces = AclApiPermission.CreateSpaces,
  CreateTeams = AclApiPermission.CreateTeams,
  DeleteOrg = AclApiPermission.DeleteOrg,
  GrantAdministrator = AclApiPermission.GrantAdministrator,
  GrantBillingAdministrator = AclApiPermission.GrantBillingAdministrator,
  GrantEditor = AclApiPermission.GrantEditor,
  GrantGuest = AclApiPermission.GrantGuest,
  GrantMember = AclApiPermission.GrantMember,
  GrantOwner = AclApiPermission.GrantOwner,
  GrantViewer = AclApiPermission.GrantViewer,
  ReadBilling = AclApiPermission.ReadBilling,
  ReadOrgData = AclApiPermission.ReadOrgData,
  ReadSubjects = AclApiPermission.ReadSubjects,
  ReceiveGrants = AclApiPermission.ReceiveGrants,
  WriteBilling = AclApiPermission.WriteBilling,
  WriteOrgData = AclApiPermission.WriteOrgData,
}

export enum AclOrgSpacePermission {
  Checkout = AclApiPermission.Checkout,
  CommitArbitraryFiles = AclApiPermission.CommitArbitraryFiles,
  Delete = AclApiPermission.Delete,
  GrantCreator = AclApiPermission.GrantCreator,
  GrantEditor = AclApiPermission.GrantEditor,
  GrantGuest = AclApiPermission.GrantGuest,
  GrantMember = AclApiPermission.GrantMember,
  GrantOwner = AclApiPermission.GrantOwner,
  GrantViewer = AclApiPermission.GrantViewer,
  ReadAllResources = AclApiPermission.ReadAllResources,
  ReadSpaceSettings = AclApiPermission.ReadSpaceSettings,
  ReadSubjects = AclApiPermission.ReadSubjects,
  ReceiveGrants = AclApiPermission.ReceiveGrants,
  Write = AclApiPermission.Write,
  WriteDwhCredentials = AclApiPermission.WriteDwhCredentials,
  WriteSpaceSettings = AclApiPermission.WriteSpaceSettings,
}

export enum AclOrgUserPermission {
  ChangePassword = AclApiPermission.ChangePassword,
  Delete = AclApiPermission.Delete,
  Impersonate = AclApiPermission.Impersonate,
  PerformResourceEnumerationAs = AclApiPermission.PerformResourceEnumerationAs,
  Read = AclApiPermission.Read,
  Write = AclApiPermission.Write,
}

export enum AclOrgTeamPermission {
  Delete = AclApiPermission.Delete,
  GrantManager = AclApiPermission.GrantManager,
  GrantMember = AclApiPermission.GrantMember,
  PerformResourceEnumerationAs = AclApiPermission.PerformResourceEnumerationAs,
  Read = AclApiPermission.Read,
  ReadSubjects = AclApiPermission.ReadSubjects,
  Write = AclApiPermission.Write,
}

export enum AclOrgAccessTokenPermission {
  Delete = AclApiPermission.Delete,
  GrantManager = AclApiPermission.GrantManager,
  GrantViewer = AclApiPermission.GrantViewer,
  PerformResourceEnumerationAs = AclApiPermission.PerformResourceEnumerationAs,
  Read = AclApiPermission.Read,
  ReadKey = AclApiPermission.ReadKey,
  ReadSubjects = AclApiPermission.ReadSubjects,
  RotateKey = AclApiPermission.RotateKey,
  Write = AclApiPermission.Write,
}

export type AclIntegrationsPermission = AclAssetModulePermission;
export const AclIntegrationsPermission = { ...AclAssetModulePermission };

export type AclModelsPermission = AclAssetModulePermission;
export const AclModelsPermission = { ...AclAssetModulePermission };

export type AclVisualizationsPermission = AclAssetModulePermission;
export const AclVisualizationsPermission = { ...AclAssetModulePermission };

export type AclOrchestrationsPermission = AclAssetModulePermission;
export const AclOrchestrationsPermission = { ...AclAssetModulePermission };

export type AclDataContractsPermission = AclAssetModulePermission;
export const AclDataContractsPermission = { ...AclAssetModulePermission };

export type AclDataExportsPermission = AclAssetModulePermission;
export const AclDataExportsPermission = { ...AclAssetModulePermission };

export type AclAlertsPermission = AclAssetModulePermission;
export const AclAlertsPermission = { ...AclAssetModulePermission };

export type AclLineagePermission = AclAssetModulePermission;
export const AclLineagePermission = { ...AclAssetModulePermission };

export type AclIntegrationPermission = AclRunnableAssetPermission;
export const AclIntegrationPermission = { ...AclRunnableAssetPermission };

export type AclUIModelPermission = AclRunnableAssetPermission;
export const AclUIModelPermission = { ...AclRunnableAssetPermission };

export type AclSQLModelPermission = AclRunnableAssetPermission;
export const AclSQLModelPermission = { ...AclRunnableAssetPermission };

export type AclVisualizationDashboardPermission = AclAssetPermission;
export const AclVisualizationDashboardPermission = { ...AclAssetPermission };

export const AclVisualizationDashboardWidgetPermission = {
  ...AclAssetPermission,
  ReadUnderlyingData: AclApiPermission.ReadUnderlyingData,
} as const;
export type AclVisualizationDashboardWidgetPermission =
  (typeof AclVisualizationDashboardWidgetPermission)[keyof typeof AclVisualizationDashboardWidgetPermission];

export const AclExportPermission = {
  ...AclAssetPermission,
  Create: AclApiPermission.Create,
} as const;
export type AclExportPermission = (typeof AclExportPermission)[keyof typeof AclExportPermission];

export type AclDataContractPermission = AclAssetPermission;
export const AclDataContractPermission = { ...AclAssetPermission };

export type AclStatusAlertPermission = AclAssetPermission;
export const AclStatusAlertPermission = { ...AclAssetPermission };

export type AclOrchestrationPermission = AclRunnableAssetPermission;
export const AclOrchestrationPermission = { ...AclRunnableAssetPermission };

export type AclLineageSnapshotPermission = AclAssetPermission;
export const AclLineageSnapshotPermission = { ...AclAssetPermission };

export type AclTablePermission = AclNonWritableResourcePermission;
export const AclTablePermission = { ...AclNonWritableResourcePermission };

export enum AclJobPermission {
  Read = AclApiPermission.Read,
  ReadSubjects = AclApiPermission.ReadSubjects,
}

export enum AclOrchestrationRunPermission {
  Read = AclApiPermission.Read,
  ReadSubjects = AclApiPermission.ReadSubjects,
}

export type AclExportTriggerPermission = AclRunnableAssetPermission;
export const AclExportTriggerPermission = { ...AclRunnableAssetPermission };

export type AclPermission =
  | AclOrgUserPermission
  | AclOrgTeamPermission
  | AclOrgAccessTokenPermission
  | AclOrgPermission
  | AclOrgSpacePermission
  | AclIntegrationsPermission
  | AclModelsPermission
  | AclVisualizationsPermission
  | AclOrchestrationsPermission
  | AclDataContractsPermission
  | AclDataExportsPermission
  | AclAlertsPermission
  | AclLineagePermission
  | AclIntegrationPermission
  | AclUIModelPermission
  | AclSQLModelPermission
  | AclVisualizationDashboardPermission
  | AclVisualizationDashboardWidgetPermission
  | AclExportPermission
  | AclDataContractPermission
  | AclStatusAlertPermission
  | AclOrchestrationPermission
  | AclTablePermission
  | AclJobPermission
  | AclOrchestrationRunPermission
  | AclExportTriggerPermission;

export interface AclHttpPermission {
  permission: AclPermission;
  allowed: boolean;
  detail?: string;
}

export interface AclPermissionDetail {
  allowed: boolean;
  detail?: string;
}

export interface AclCheckResourcePermissionsHttpModel {
  permissions: AclHttpPermission[];
  resource_id: string;
  resource_type: AclResourceType;
}

export type AclPermissionsRecord = Record<AclPermission, AclPermissionDetail>;
export type AclPermissionsBySpaceId = Record<string, AclPermissionsRecord>;

export type AclPermissionsByResourceId = Record<string, AclPermissionsRecord | AclPermissionsBySpaceId>;
export type AclPermissionsState = Partial<Record<AclResourceType, AclPermissionsByResourceId>>;
